import {
  Box,
  Button,
  Flex,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Text,
  VStack,
  SimpleGrid,
  Input,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
} from "@chakra-ui/react";
import { Field, Form, Formik } from "formik";
import React, { useEffect, useState, useMemo } from "react";
import { BiLowVision, BiShow } from "react-icons/bi";
import { SearchIcon, ArrowLeftIcon } from "@chakra-ui/icons";
import { FaBookOpen } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getAllSubjectsRequest } from "../../modules/admin-modules/subjects/Actions";
import { loginUserAction } from "../../modules/auth/Actions";
import PublicLayout from "../public/Layout/layout";

const LoginForm = (props) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.auth.isLoading);
  const [inputType, setInputType] = useState("password");
  const subjects = useSelector((state) => state.subjects.subjects);
  const [currentStep, setCurrentStep] = useState(1); // 1: Subject selection, 2: Login form
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    dispatch(getAllSubjectsRequest());
  }, []);

  // Filter subjects based on search
  const filteredSubjects = useMemo(() => {
    return subjects.filter(subject => {
      const matchesSearch = subject.name.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [subjects, searchTerm]);

  const validateUserName = (value) => {
    let error;
    if (!value) {
      error = "Required";
    }
    return error;
  };

  const validatePassword = (value) => {
    let error;
    if (!value) {
      error = "Required";
    }
    return error;
  };

  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
    setCurrentStep(2);
  };

  const handleBackToSubjects = () => {
    setCurrentStep(1);
    setSelectedSubject(null);
  };

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const baseUrl = "https://api.asquera.com/";

  // Step 1: Subject Selection
  const renderSubjectSelection = () => (
    <VStack spacing={8} align="stretch" w="100%">
      <VStack spacing={4} align="center">
        <Heading 
          textAlign="center" 
          mb="6"
          fontSize={{ base: "28px", md: "36px" }}
          fontWeight="800"
          color="gray.800"
          fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
        >
          اختر المادة الدراسية
        </Heading>

      </VStack>
      
      {/* Search Bar */}
      <Box>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="orange.400" />
          </InputLeftElement>
          <Input
            placeholder="ابحث عن المادة..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="lg"
            borderRadius="xl"
            border="2px"
            borderColor="gray.200"
            bg="white"
            _focus={{
              borderColor: 'orange.500',
              boxShadow: '0 0 0 3px rgba(255, 140, 0, 0.1)',
            }}
            _hover={{
              borderColor: 'orange.300',
            }}
            fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
          />
        </InputGroup>
      </Box>

      {/* Results Count */}
      <Text 
        fontSize="sm" 
        color="gray.600"
        fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
      >
        تم العثور على {filteredSubjects.length} مادة
      </Text>

      {/* Subject List */}
      <Box>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {filteredSubjects.map((subject) => (
            <Button
              key={subject.id}
              onClick={() => handleSubjectSelect(subject)}
              variant="outline"
              height="auto"
              p={8}
              textAlign="left"
              borderRadius="xl"
              borderWidth="2px"
              borderColor="gray.200"
              bg="white"
              _hover={{
                borderColor: 'orange.400',
                bg: 'orange.50',
                shadow: '0 10px 25px rgba(255, 140, 0, 0.15)',
                transform: 'translateY(-4px)',
              }}
              _active={{
                transform: 'scale(0.98)',
              }}
              transition="all 0.3s ease"
              boxShadow="0 4px 15px rgba(0,0,0,0.08)"
            >
              <Flex justify="space-between" align="flex-start" width="100%">
                <VStack align="start" spacing={3} flex={1} minW={0}>
                  <Text 
                    fontSize="lg" 
                    fontWeight="700" 
                    wordBreak="break-word" 
                    width="100%" 
                    whiteSpace="normal" 
                    overflowWrap="break-word"
                    color="gray.800"
                    fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                    lineHeight="1.4"
                  >
                    {subject.name}
                  </Text>
                  <Text 
                    fontSize="sm" 
                    color="gray.600"
                    fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                  >
                    اضغط للاختيار
                  </Text>
                </VStack>

                {subject.logo ? (
                  <Box
                    width="90px"
                    height="90px"
                    borderRadius="xl"
                    overflow="hidden"
                    bg="orange.50"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    ml={4}
                    flexShrink={0}
                    border="2px solid"
                    borderColor="orange.100"
                    boxShadow="0 4px 15px rgba(255, 140, 0, 0.1)"
                  >
                    <img
                      src={baseUrl + subject.logo}
                      alt={subject.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                ) : (
                  <Box
                    width="90px"
                    height="90px"
                    borderRadius="xl"
                    overflow="hidden"
                    bg="orange.50"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    ml={4}
                    flexShrink={0}
                    border="2px solid"
                    borderColor="orange.100"
                    boxShadow="0 4px 15px rgba(255, 140, 0, 0.1)"
                  >
                    <img
                      src={"/logo.png"}
                      alt={subject.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                )}
              </Flex>
            </Button>
          ))}
        </SimpleGrid>

        {filteredSubjects.length === 0 && (
          <VStack py={16} spacing={6}>
            <Icon as={FaBookOpen} boxSize={16} color="orange.400" />
            <Text 
              fontSize="xl" 
              fontWeight="700" 
              color="gray.800"
              fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
            >
              لم يتم العثور على مواد
            </Text>
            <Text 
              color="gray.600"
              textAlign="center"
              fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
            >
              جرب تعديل البحث أو معايير التصفية
            </Text>
          </VStack>
        )}
      </Box>
    </VStack>
  );

  // Step 2: Login Form
  const renderLoginForm = () => (
    <Formik
      initialValues={{
        phone: "",
        password: "",
        subject_id: selectedSubject?.id || null,
      }}
      onSubmit={(values) => {
        let formData = new FormData();
        formData.append("phone", values.phone);
        formData.append("password", values.password);
        formData.append("subject_id", values.subject_id);

        dispatch(loginUserAction(formData));
      }}
    >
      {({ errors, touched, values, setFieldValue }) => {
        // Update form value when subject is selected
        if (selectedSubject && values.subject_id !== selectedSubject.id) {
          setFieldValue("subject_id", selectedSubject.id);
        }

        return (
          <Form>
            <VStack spacing={8} align="stretch" w="100%">
              {/* Back Button and Header */}
              <Flex justify="space-between" align="center">
                <Button
                  leftIcon={<ArrowLeftIcon />}
                  variant="ghost"
                  onClick={handleBackToSubjects}
                  color="gray.600"
                  _hover={{ 
                    bg: 'orange.50',
                    color: 'orange.600'
                  }}
                  fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                  fontWeight="600"
                >
                  العودة للمواد
                </Button>
                <Heading 
                  size="lg"
                  color="gray.800"
                  fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                  fontWeight="800"
                >
                  تسجيل الدخول
                </Heading>
                <Box w="40px" /> {/* Spacer for centering */}
              </Flex>

              {/* Selected Subject Display */}
              {selectedSubject && (
                <Box
                  p={6}
                  bg="orange.50"
                  border="2px"
                  borderColor="orange.200"
                  borderRadius="xl"
                  boxShadow="0 4px 15px rgba(255, 140, 0, 0.1)"
                >
                  <Text 
                    fontSize="md" 
                    color="orange.700" 
                    fontWeight="700"
                    fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                  >
                    المادة المختارة: {selectedSubject.name}
                  </Text>
                </Box>
              )}

              {/* Phone Input */}
              <Box>
                <FormLabel
                  htmlFor="phone"
                  color="gray.700"
                  fontSize="18px"
                  fontWeight="700"
                  mb={3}
                  fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                >
                  رقم الهاتف
                </FormLabel>

                <Field
                  as={Input}
                  name="phone"
                  validate={validateUserName}
                  placeholder="أدخل رقم هاتفك"
                  size="lg"
                  borderRadius="xl"
                  border="2px"
                  borderColor="gray.200"
                  bg="white"
                  _focus={{
                    borderColor: 'orange.500',
                    boxShadow: '0 0 0 3px rgba(255, 140, 0, 0.1)',
                  }}
                  _hover={{
                    borderColor: 'orange.300',
                  }}
                  fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                />
                {errors.phone && touched.phone && (
                  <Text color="red.500" fontSize="sm" mt={2} fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif">
                    {errors.phone}
                  </Text>
                )}
              </Box>

              {/* Password Input */}
              <Box position="relative">
                <FormLabel
                  htmlFor="password"
                  color="gray.700"
                  fontSize="18px"
                  fontWeight="700"
                  mb={3}
                  fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                >
                  كلمة المرور
                </FormLabel>

                <Field
                  as={Input}
                  name="password"
                  validate={validatePassword}
                  type={inputType}
                  placeholder="أدخل كلمة المرور"
                  size="lg"
                  borderRadius="xl"
                  border="2px"
                  borderColor="gray.200"
                  bg="white"
                  pr="50px"
                  _focus={{
                    borderColor: 'orange.500',
                    boxShadow: '0 0 0 3px rgba(255, 140, 0, 0.1)',
                  }}
                  _hover={{
                    borderColor: 'orange.300',
                  }}
                  fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                />

                <Icon
                  position="absolute"
                  right="15px"
                  top="50px"
                  fontSize="20px"
                  color="gray.500"
                  cursor="pointer"
                  _hover={{ color: "orange.500" }}
                  as={inputType == "password" ? BiShow : BiLowVision}
                  onClick={() =>
                    inputType == "password"
                      ? setInputType("text")
                      : setInputType("password")
                  }
                />
                {errors.password && touched.password && (
                  <Text color="red.500" fontSize="sm" mt={2} fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif">
                    {errors.password}
                  </Text>
                )}
              </Box>

              {/* Login Button */}
              <Button
                type="submit"
                w="full"
                p="20px"
                alignSelf="center"
                isLoading={isLoading}
                bg="orange.500"
                color="white"
                mt="20px"
                borderRadius="xl"
                fontSize="18px"
                fontWeight="700"
                _hover={{
                  bg: "orange.600",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 25px rgba(255, 140, 0, 0.3)"
                }}
                _active={{ transform: "translateY(0)" }}
                transition="all 0.3s ease"
                boxShadow="0 4px 15px rgba(255, 140, 0, 0.2)"
                fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
              >
                تسجيل الدخول
              </Button>
            </VStack>
          </Form>
        );
      }}
    </Formik>
  );

  return (
    <PublicLayout color="main_1" bg="white" register={false}>
      <Flex
        flexDirection="column"
        alignItems="center"
        my="50px"
        w="100%"
        h="100%"
      >
        <Box
          sx={{ 
            width: { base: "95%", md: currentStep === 1 ? "90%" : "40%" },
            maxWidth: currentStep === 1 ? "1200px" : "500px"
          }}
        >
          {currentStep === 1 ? renderSubjectSelection() : renderLoginForm()}
        </Box>
      </Flex>
    </PublicLayout>
  );
};

export default LoginForm;
